/* eslint-disable react/prop-types */
/**
 * Right-panel components for ChatsPage (iter 134 split).
 *
 * Extracted from the 3,440-line ChatsPage.jsx as part of the P1 component
 * split.  These are the presentational tabs that render on the right
 * side of the chat view:
 *
 *   • InfoTab   — customer contact, WATI status, dept, labels
 *   • OrderTab  — customer form, order placement, existing orders
 *   • AiTab     — AI suggest-reply / sentiment / categorize buttons
 *
 * Plus the internal helpers they share (Sec, Row, CustInput, AiBtn,
 * CategoryBadge).
 *
 * NOTE: these components take their state via props — no local business
 * logic — so this extraction is zero-risk. Only presentation moves.
 */
import React, { useMemo } from "react";
import { toast } from "sonner";
import {
  User, Phone, Tag, ShoppingBag, Brain, X, Check, Plus,
  Mail, MapPin, IndianRupee, Package, CreditCard, Clipboard,
  Sparkles, Loader2,
} from "lucide-react";
import AdHocCouponCard from "./AdHocCouponCard";

/* ================================================================= */
/* Shared constants                                                   */
/* ================================================================= */

export const PRESET_LABELS = [
  "New Customer", "Payment Pending", "Paid", "Order Complete",
  "Follow Up", "VIP", "Negotiation", "Urgent",
];

const LABEL_COLORS = [
  { bg: "#FFF3E5", text: "#B45309", border: "#F59E0B", dot: "#F59E0B" },
  { bg: "#E0F2FE", text: "#075985", border: "#38BDF8", dot: "#38BDF8" },
  { bg: "#DCFCE7", text: "#166534", border: "#4ADE80", dot: "#4ADE80" },
  { bg: "#FCE7F3", text: "#9D174D", border: "#F472B6", dot: "#F472B6" },
  { bg: "#EDE9FE", text: "#5B21B6", border: "#A78BFA", dot: "#A78BFA" },
  { bg: "#FEE2E2", text: "#991B1B", border: "#F87171", dot: "#F87171" },
  { bg: "#CFFAFE", text: "#155E75", border: "#22D3EE", dot: "#22D3EE" },
  { bg: "#F3E8FF", text: "#6B21A8", border: "#C084FC", dot: "#C084FC" },
];

export const getLabelColor = (label) =>
  LABEL_COLORS[Math.abs([...label].reduce((a, c) => a + c.charCodeAt(0), 0)) % LABEL_COLORS.length];

const CATEGORY_COLORS = {
  Books: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  Sculptures: { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  Paintings: { bg: "bg-pink-50", text: "text-pink-700", dot: "bg-pink-500" },
  Clothing: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  Jewelry: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  "Home and Living": { bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-500" },
};
const DEFAULT_CAT_COLOR = { bg: "bg-gray-50", text: "text-gray-600", dot: "bg-gray-400" };
const catColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_CAT_COLOR;

export const PAYMENT_MODES_CHAT = ["COD", "UPI", "Card", "Net Banking", "Bank Transfer", "Special Pay", "Proforma"];

/* ================================================================= */
/* Internal building blocks                                           */
/* ================================================================= */

export const Sec = ({ title, children }) => (
  <div>
    <h4 className="text-[10px] font-semibold text-[--text-muted] uppercase tracking-wider mb-2">{title}</h4>
    <div className="space-y-1.5">{children}</div>
  </div>
);

export const Row = ({ icon, text }) => (
  <div className="flex items-center gap-2">
    <span className="text-[--text-muted] flex-shrink-0">{icon}</span>
    <span className="text-[12px] text-[--text-primary]">{text}</span>
  </div>
);

export const CategoryBadge = ({ cat }) => {
  const cc = catColor(cat);
  return (
    <span
      className={`text-[8px] px-1.5 py-0.5 ${cc.bg} ${cc.text} rounded-full font-semibold flex-shrink-0 whitespace-nowrap`}
      data-testid={`cat-badge-${cat}`}
    >
      {cat}
    </span>
  );
};

const CustInput = ({ icon, placeholder, value, onChange, testId, type = "text" }) => (
  <div className="flex items-center gap-2 bg-white border border-[--border] rounded-lg px-2 py-1.5">
    <span className="text-[--text-muted] flex-shrink-0">{icon}</span>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 text-xs bg-transparent outline-none placeholder:text-[--text-muted]"
      data-testid={testId}
    />
  </div>
);

const AiBtn = ({ icon, label, desc, onClick, loading, testId, color }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`w-full flex items-center gap-3 p-3 rounded-xl border border-[--border] hover:border-${color}-300 hover:bg-${color}-50/50 transition-all text-left group`}
    data-testid={testId}
  >
    <span className={`w-8 h-8 rounded-lg bg-${color}-50 text-${color}-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
      {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
    </span>
    <div>
      <p className="text-[12px] font-semibold text-[--text-primary]">{label}</p>
      <p className="text-[10px] text-[--text-muted]">{desc}</p>
    </div>
  </button>
);

/* ================================================================= */
/* InfoTab — customer contact, WATI status, department, labels       */
/* ================================================================= */

export const InfoTab = ({
  lead, agents, onReassign, user, onWatiStatus, onSetDepartment,
  allLabels, onAddLabel, onRemoveLabel,
  newLabelText, setNewLabelText, showLabelInput, setShowLabelInput,
  // Iter 146 (Feb 2026) — Yash asked that the WATI status controls be
  // hidden on the Email ticket panel (they don't apply — emails live on
  // Zoho). Consumers can pass `hideWatiStatus` to omit that section.
  hideWatiStatus = false,
  // `extraStatusSlot` lets the Email panel inject a "Zoho Ticket Status"
  // block in place of the WATI one.
  extraStatusSlot = null,
}) => {
  // Iter 156.5 — memoise the label-suggestions list. Was recomputing the
  // Set-dedup + filter on every keystroke into `newLabelText` because the
  // datalist sits inside the controlled-input branch.
  const labelSuggestions = useMemo(
    () => [...new Set([...PRESET_LABELS, ...allLabels])]
      .filter((l) => !(lead.labels || []).includes(l)),
    [allLabels, lead.labels]
  );
  return (
  <div className="space-y-4">
    <Sec title="Contact">
      <Row icon={<User size={13} />} text={lead.name} />
      <Row icon={<Phone size={13} />} text={lead.phone} />
      {/* Iter 156.38c (Feb 28, 2026, Yash) — copyable Lead ID. Operators
          need this for `/api/admin/auto-reply-diagnose/{lead_id}` etc.
          The chat URL doesn't expose it (state-only routing). */}
      {lead.id && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono"
             data-testid="lead-id-row">
          <span className="select-all" data-testid="lead-id-value">ID: {lead.id}</span>
          <button
            type="button"
            onClick={() => {
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(lead.id);
                toast.success("Lead ID copied");
              }
            }}
            className="hover:text-blue-600 underline decoration-dotted"
            data-testid="lead-id-copy-btn"
            title="Copy lead ID"
          >
            copy
          </button>
        </div>
      )}
      {/* Iter 132 — render WATI status badge (single source of truth). */}
      {(lead.wati_status || lead.status) && (() => {
        const ws = lead.wati_status || "OPEN";
        const isPlaced = lead.status === "placed_order";
        const cls = isPlaced
          ? "bg-green-50 text-green-700"
          : ws === "SOLVED" ? "bg-gray-100 text-gray-600"
          : ws === "EXPIRED" ? "bg-orange-50 text-orange-700"
          : ws === "PENDING" ? "bg-yellow-50 text-yellow-700"
          : "bg-blue-50 text-blue-700";
        const label = isPlaced ? "placed_order" : ws.toLowerCase();
        return <Row icon={<Tag size={13} />} text={<span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>} />;
      })()}
      {lead.category && <Row icon={<ShoppingBag size={13} />} text={<CategoryBadge cat={lead.category} />} />}
      {lead.sentiment && (
        <Row
          icon={<Brain size={13} />}
          text={<span className={`text-[11px] font-medium ${lead.sentiment === "negative" ? "text-red-600" : lead.sentiment === "positive" ? "text-green-600" : "text-gray-500"}`}>{lead.sentiment}</span>}
        />
      )}
    </Sec>

    {/*
      Iter 156.24 — refund-verify warning chip.
      When a cancellation hit the "upstream success + no refund_id" path
      (admin_crm.php may have silently no-op'd), `refund_verify_pending`
      is set on the lead doc. Surface it here so CS doesn't have to
      grep backend.err.log. Renders only when the flag is present.
    */}
    {lead.refund_verify_pending && (
      <Sec title="⚠️ Refund Verify Pending">
        <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 space-y-1.5 text-[11px] text-red-700" data-testid="refund-verify-pending-chip">
          <div className="font-semibold">
            Order #{lead.refund_verify_pending.order_id} · {lead.refund_verify_pending.itemcode}
          </div>
          <div>
            Refund of ₹{lead.refund_verify_pending.amount} queued — but upstream returned no refund_id.
            Verify on admin_crm.php and initiate refund on payment gateway if needed.
          </div>
          {/* Iter 156.25 — surface verification verdict if present. */}
          {lead.refund_verify_pending.verification_status === "noop" && (
            <div className="font-semibold text-red-800 bg-red-100 px-1.5 py-0.5 rounded">
              ⚠ Verification: SILENT UPSTREAM NO-OP — cancel likely did NOT execute. Run the cancel manually.
            </div>
          )}
          {lead.refund_verify_pending.verification_status === "inconclusive" && (
            <div className="text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
              Verification: inconclusive — cannot confirm cancel actually applied.
            </div>
          )}
          {lead.refund_verify_pending.audit_id && (
            <div className="text-[10px] opacity-70">audit: {lead.refund_verify_pending.audit_id}</div>
          )}
        </div>
      </Sec>
    )}

    <Sec title="Labels">
      <div className="flex flex-wrap gap-1.5">
        {(lead.labels || []).map((l) => {
          const lc = getLabelColor(l);
          return (
            <span
              key={l}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold"
              style={{ background: lc.bg, color: lc.text, border: `1px solid ${lc.border}` }}
              data-testid={`info-label-${l}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: lc.dot }} />
              {l}
              <button onClick={() => onRemoveLabel(l)} className="ml-0.5 hover:opacity-50" data-testid={`remove-label-${l}`}>
                <X size={10} />
              </button>
            </span>
          );
        })}
        {showLabelInput ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={newLabelText}
              onChange={(e) => setNewLabelText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onAddLabel(newLabelText);
                if (e.key === "Escape") { setShowLabelInput(false); setNewLabelText(""); }
              }}
              placeholder="Label name"
              autoFocus
              className="text-[10px] border border-green-200 rounded-lg px-2 py-1 w-28 outline-none focus:ring-1 focus:ring-green-300"
              data-testid="new-label-input"
              list="label-suggestions"
            />
            <datalist id="label-suggestions">
              {labelSuggestions.map((l) => <option key={l} value={l} />)}
            </datalist>
            <button onClick={() => onAddLabel(newLabelText)} className="text-green-600 hover:text-green-800" data-testid="confirm-label-btn">
              <Check size={12} />
            </button>
            <button onClick={() => { setShowLabelInput(false); setNewLabelText(""); }} className="text-gray-400 hover:text-red-500">
              <X size={12} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowLabelInput(true)}
            className="inline-flex items-center gap-0.5 text-[10px] text-green-600 hover:text-green-800 font-medium border border-dashed border-green-300 rounded-full px-2 py-0.5 hover:bg-green-50 transition-colors"
            data-testid="add-label-btn"
          >
            <Plus size={11} /> Add Label
          </button>
        )}
      </div>
    </Sec>

    <Sec title="Assigned To (CRM)">
      <select
        value={lead.assigned_to || ""}
        onChange={(e) => onReassign(e.target.value)}
        className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-[--accent] outline-none"
        data-testid="reassign-select"
      >
        <option value="">Unassigned</option>
        {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </Sec>

    <Sec title="Department">
      <div className="flex gap-1.5" data-testid="department-section">
        {["Sales", "Customer Support"].map((d) => {
          const active = (lead.department || "Sales") === d;
          return (
            <button
              key={d}
              onClick={() => onSetDepartment && onSetDepartment(d)}
              className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all border ${active ? "shadow-sm" : "bg-white text-[--text-muted] border-[--border-dark] hover:border-gray-300"}`}
              style={active ? (d === "Sales"
                ? { background: "#ECFDF5", color: "#047857", borderColor: "#6EE7B7" }
                : { background: "#EEF2FF", color: "#4338CA", borderColor: "#A5B4FC" }) : {}}
              data-testid={`set-dept-${d.toLowerCase().replace(" ", "-")}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </Sec>

    {!hideWatiStatus && (
      <Sec title="Status">
        {/* Iter 153 (Feb 6, 2026, Yash) — CRM owns ticket status now.
            Section renamed from "WATI Ticket Status" → "Status".
            PENDING dropped from the enum. EXPIRED added so agents can
            manually mark a chat expired if needed. */}
        <div className="flex gap-1" data-testid="status-panel">
          {["OPEN", "SOLVED", "EXPIRED"].map((s) => {
            // Treat legacy PENDING as OPEN for the highlight.
            const cur = ((lead?.wati_status || "OPEN").toUpperCase() === "PENDING")
              ? "OPEN"
              : (lead?.wati_status || "OPEN").toUpperCase();
            const active = cur === s;
            const colors = s === "OPEN"
              ? { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD" }
              : s === "SOLVED"
              ? { bg: "#D1FAE5", text: "#047857", border: "#6EE7B7" }
              : { bg: "#FED7AA", text: "#9A3412", border: "#FDBA74" };
            return (
              <button
                key={s}
                onClick={() => onWatiStatus && onWatiStatus(s)}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-bold transition-all border-2 ${active ? "shadow-sm" : "bg-white border-[--border-dark] text-[--text-muted] hover:border-gray-300"}`}
                style={active ? { background: colors.bg, color: colors.text, borderColor: colors.border } : {}}
                data-testid={`status-btn-${s.toLowerCase()}`}
              >
                {s}
              </button>
            );
          })}
        </div>
        <p className="text-[9px] text-[--text-muted] mt-1">Auto-expires 24h after last customer message. Customer replies auto-reopen the ticket.</p>
      </Sec>
    )}

    {/* Iter 146 — slot for consumer-provided status controls (e.g. the
        Email panel injects a Zoho Ticket Status block here). */}
    {extraStatusSlot}

    <AdHocCouponCard leadId={lead?.id} lead={lead} />
  </div>
  );
};

/* ================================================================= */
/* OrderTab — customer form + place / record order                   */
/* ================================================================= */

export const OrderTab = ({
  lead, custForm, setCustForm, savingCust,
  onSave, onPlaceOrder, onAiExtract, aiLoading,
  orderNum, setOrderNum, orderAmt, setOrderAmt, onRecordOrder,
}) => (
  <div className="space-y-4">
    <Sec title="Customer Details">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-[--text-muted]">Fill manually or auto-extract</span>
        <button onClick={onAiExtract} disabled={!!aiLoading} className="text-[10px] text-purple-600 hover:underline flex items-center gap-1" data-testid="ai-extract-btn">
          {aiLoading === "extract" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Auto-fill from chat
        </button>
      </div>
      <CustInput icon={<User size={12} />} placeholder="Name" value={custForm.customer_name} onChange={(v) => setCustForm((p) => ({ ...p, customer_name: v }))} testId="cust-name" />
      <CustInput icon={<Mail size={12} />} placeholder="Email" value={custForm.customer_email} onChange={(v) => setCustForm((p) => ({ ...p, customer_email: v }))} testId="cust-email" />
      <CustInput icon={<Phone size={12} />} placeholder="Phone" value={custForm.customer_phone} onChange={(v) => setCustForm((p) => ({ ...p, customer_phone: v }))} testId="cust-phone" />
      <CustInput icon={<MapPin size={12} />} placeholder="Address" value={custForm.customer_address} onChange={(v) => setCustForm((p) => ({ ...p, customer_address: v }))} testId="cust-address" />
      <CustInput icon={<MapPin size={12} />} placeholder="Pincode" value={custForm.customer_pincode} onChange={(v) => setCustForm((p) => ({ ...p, customer_pincode: v }))} testId="cust-pincode" />
    </Sec>
    <Sec title="Notes">
      <textarea
        value={custForm.notes}
        onChange={(e) => setCustForm((p) => ({ ...p, notes: e.target.value }))}
        placeholder="Add notes about this customer or order..."
        rows={3}
        className="w-full bg-white border border-[--border] rounded-lg px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-[--accent] outline-none resize-none placeholder:text-[--text-muted]"
        data-testid="order-notes"
      />
    </Sec>
    <Sec title="Order Details">
      <CustInput icon={<IndianRupee size={12} />} placeholder="Amount (INR)" value={custForm.amount} onChange={(v) => setCustForm((p) => ({ ...p, amount: v }))} testId="order-amount" type="number" />
      {/* Iter 153.3 (Feb 7, 2026, Yash) — Negotiated discount % field
          REMOVED. Per Sales SOP we only use custom-discount-amount + COD
          fields. When the AI extracts a percent we auto-compute the
          rupee value (amount × percent ÷ 100) and pre-fill this input. */}
      <CustInput icon={<IndianRupee size={12} />} placeholder="Custom Discount (INR)" value={custForm.discount_amount} onChange={(v) => setCustForm((p) => ({ ...p, discount_amount: v, discount_percent: "" }))} testId="order-discount-amt" type="number" />
      <CustInput icon={<Package size={12} />} placeholder="Item Codes (comma separated)" value={custForm.item_codes} onChange={(v) => setCustForm((p) => ({ ...p, item_codes: v }))} testId="order-item-codes" />
      <div className="flex items-center gap-2 bg-white border border-[--border] rounded-lg px-2 py-1.5">
        <CreditCard size={12} className="text-[--text-muted] flex-shrink-0" />
        <select
          value={custForm.payment_mode}
          onChange={(e) => setCustForm((p) => ({ ...p, payment_mode: e.target.value }))}
          className="flex-1 text-xs bg-transparent outline-none"
          data-testid="order-payment-mode"
        >
          {PAYMENT_MODES_CHAT.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="flex gap-1.5 pt-1">
        <button onClick={onSave} disabled={savingCust} className="btn-secondary text-[11px] py-1.5 px-3 flex-1" data-testid="save-details-btn">
          {savingCust ? <Loader2 size={11} className="animate-spin" /> : <><Clipboard size={11} /> Save</>}
        </button>
        <button onClick={onPlaceOrder} disabled={savingCust} className="btn-primary text-[11px] py-1.5 px-3 flex-1 flex items-center justify-center gap-1" data-testid="place-admin-order-btn">
          {savingCust ? <Loader2 size={11} className="animate-spin" /> : <><Package size={11} /> Place Admin Order</>}
        </button>
      </div>
    </Sec>
    <Sec title="Record Existing Order">
      <CustInput icon={<Package size={12} />} placeholder="Order Number (e.g. EXO-27488474)" value={orderNum} onChange={setOrderNum} testId="record-order-num" />
      <CustInput icon={<Tag size={12} />} placeholder="Amount (INR)" value={orderAmt} onChange={setOrderAmt} testId="record-order-amt" type="number" />
      <button onClick={onRecordOrder} className="w-full btn-secondary text-[11px] py-1.5 flex items-center justify-center gap-1" data-testid="record-order-btn">
        <Check size={11} /> Record Order
      </button>
    </Sec>
    {lead.orders?.length > 0 && (
      <Sec title={`Orders (${lead.orders.length})`}>
        {lead.orders.map((o) => (
          <div key={o.id} className="p-2 bg-[--bg-cream] rounded-lg text-xs">
            <div className="flex justify-between">
              <span className="font-semibold">{o.order_number}</span>
              {o.order_type && <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full">{o.order_type}</span>}
            </div>
            {o.amount > 0 && (
              <p className="text-[--text-secondary] mt-0.5">
                INR {o.amount.toLocaleString()}
                {o.final_amount && o.final_amount !== o.amount && (
                  <span className="text-green-600 ml-1">(Final: INR {o.final_amount.toLocaleString()})</span>
                )}
              </p>
            )}
            {o.payment_mode && <p className="text-[--text-muted] text-[10px]">{o.payment_mode}{o.status ? ` - ${o.status}` : ""}</p>}
          </div>
        ))}
      </Sec>
    )}
  </div>
);

/* ================================================================= */
/* AiTab — AI action buttons                                         */
/* ================================================================= */

export const AiTab = ({ lead, onSuggest, onExtract, onSentiment, onCategorize, aiLoading }) => (
  <div className="space-y-3">
    <p className="text-[11px] text-[--text-muted]">AI-powered features for this conversation</p>
    <AiBtn icon={<Sparkles size={14} />} label="Suggest Reply" desc="Generate a contextual reply" onClick={onSuggest} loading={aiLoading === "suggest"} testId="ai-tab-suggest" color="purple" />
    <AiBtn icon={<Clipboard size={14} />} label="Extract Details" desc="Auto-fill customer info from chat" onClick={onExtract} loading={aiLoading === "extract"} testId="ai-tab-extract" color="blue" />
    <AiBtn icon={<Brain size={14} />} label="Detect Sentiment" desc="Analyze customer mood" onClick={onSentiment} loading={aiLoading === "sentiment"} testId="ai-tab-sentiment" color="amber" />
    <AiBtn icon={<ShoppingBag size={14} />} label="Smart Categorize" desc="Detect product interest" onClick={onCategorize} loading={aiLoading === "categorize"} testId="ai-tab-categorize" color="teal" />
    {lead.sentiment && (
      <div className={`p-2.5 rounded-lg text-xs ${
        lead.sentiment === "negative" ? "bg-red-50 text-red-700"
        : lead.sentiment === "positive" ? "bg-green-50 text-green-700"
        : lead.sentiment === "urgent" ? "bg-orange-50 text-orange-700"
        : "bg-gray-50 text-gray-600"
      }`}>
        Current sentiment: <strong>{lead.sentiment}</strong>
      </div>
    )}
  </div>
);
